import { useLocalStorage } from "@sazka/utils/use-storage";

//list of active ab tests to prevent logging inactive tests to analytics
export enum ActiveFrontendAbTests {
    None = "none", //in case there are no active ab tests
}

type AbTestDefaultAnalytics = {
    [key in ActiveFrontendAbTests]: boolean;
};

const abTestDefaultAnalytics: AbTestDefaultAnalytics = {
    [ActiveFrontendAbTests.None]: true,
};

interface AbTestVariantConfiguration {
    codename: string;
    userPercentage: number;
}

export interface AbTestConfiguration {
    testName: ActiveFrontendAbTests;
    variants: AbTestVariantConfiguration[];
}

export interface AbTestSelectedVariant {
    testId: ActiveFrontendAbTests;
    variantId: string;
}

export const abTestsStorageName = "ab-tests";
const store = useLocalStorage();

const getSavedTests = (): AbTestSelectedVariant[] => {
    let tests = store.getJson<AbTestSelectedVariant[]>(abTestsStorageName);
    if (!tests) {
        tests = [];
    }

    return tests;
};

const getAndCheckSavedVariant = (config: AbTestConfiguration, tests: AbTestSelectedVariant[]): string => {
    const test = tests.find((v) => v.testId == config.testName);

    if (test?.variantId) {
        if (config.variants.some((v) => v.codename == test.variantId)) {
            return test.variantId;
        } else {
            //remove variant in case variant codename is invalid
            tests.splice(tests.indexOf(test), 1);
        }
    }
};

const selectRandomVariant = (variants: AbTestVariantConfiguration[]): string => {
    //select variant with probability given by variants user percentage
    const random = Math.floor(Math.random() * variants.reduce((sum, x) => sum + x.userPercentage, 0));
    let sum = 0;
    for (const variant of variants) {
        sum += variant.userPercentage;
        if (random <= sum) {
            return variant.codename;
        }
    }
};

const saveVariant = (testName: ActiveFrontendAbTests, variant: string, tests: AbTestSelectedVariant[]) => {
    tests.push({ testId: testName, variantId: variant });
    store.setJson(abTestsStorageName, tests, "essential");
};

export const getAbTestVariant = (config: AbTestConfiguration): string => {
    const tests = getSavedTests();
    let variant = getAndCheckSavedVariant(config, tests);
    if (variant) {
        return variant;
    }

    variant = selectRandomVariant(config.variants);
    saveVariant(config.testName, variant, tests);

    return variant;
};

export const useDefaultAbTestAnalytics = (testName: ActiveFrontendAbTests): boolean => {
    return abTestDefaultAnalytics[testName] ?? false;
};
